const isString = (value: unknown): value is string => typeof value === 'string';

const safeJSONParse = <T>(value: string, fallback: T): T => {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

/**
 * Utility for handling localStorage with type safety and error handling
 */

export const localStorageUtils = {
  /**
   * Get item from localStorage with type safety
   * @param key Storage key
   * @param defaultValue Default value if key not found or parsing fails
   * @returns Parsed data or default value
   */
  getItem<T>(key: string, defaultValue: T): T {
    if (typeof window === 'undefined') {
      return defaultValue;
    }

    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue;
      }

      if (isString(defaultValue)) {
        return (item as T) || defaultValue;
      }

      return safeJSONParse(item, defaultValue);
    } catch (error) {
      console.error(`Error retrieving ${key} from localStorage:`, error);
      return defaultValue;
    }
  },

  /**
   * Set item in localStorage with type safety
   * @param key Storage key
   * @param value Value to store
   */
  setItem<T>(key: string, value: T): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const serializedValue = isString(value) ? value : JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
    } catch (error) {
      console.error(`Error storing ${key} in localStorage:`, error);
    }
  },

  /**
   * Remove item from localStorage
   * @param key Storage key
   */
  removeItem(key: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing ${key} from localStorage:`, error);
    }
  },
};
